import React, { useEffect, useMemo, useRef, useState } from "react";
import { IoIosArrowDown } from "react-icons/io";
import { useWallet } from "../../../hooks/useWallet";
import { chains } from "../../../services/chains";
import { ethers } from "ethers";
import { useAuth } from "../../../hooks/useAuth";
import { toast } from "react-toastify";
import axios from "axios";

const RouletteWallet = ({
  className,
  isActive,
  logoUrl,
  newBalance,
  setAffectedBalance,
}) => {
  const [selected, setSelected] = useState("deposit");
  const [depositAmount, setDepositAmount] = useState("");
  const [withdrawAmount, setWithdrawAmount] = useState("");
  const [balance, setBalance] = useState(0);
  const [fees, setFees] = useState(0);
  const [offChainBalance, setOffChainBalance] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const wallet = useWallet();
  const auth = useAuth();

  useEffect(() => {
    const getBalance = async () => {
      try {
        setIsLoading(true);
        const [err1, onChainBalance] = await wallet.safeCallContract({
          name: "Token3",
          method: "balanceOf",
          args: [wallet.address],
        });

        if (err1) {
          console.error("Error fetching balance:", err1);
          toast.error("Failed to fetch balance");
          return;
        }

        console.log(
          "🚀 ~ getBalance ~ onChainBalance:",
          Number(ethers.utils.formatEther(onChainBalance))
        );
        setBalance(Number(ethers.utils.formatEther(onChainBalance)));
        setAffectedBalance(Date.now() + Math.random());
        // OFF CHAIN BALANCE
        const response = await axios.get(
          `/api/game/wallet?chainId=${wallet.chainId}&gameType=roulette`
        );
        if (response) {
          console.log("🚀 ~ fetchBalance ~ response:", response);
          setOffChainBalance(response?.balance);
        }
      } catch (error) {
        console.error("Error:", error);
        toast.error("Failed to fetch balance");
      } finally {
        setIsLoading(false);
      }
    };

    if (wallet.address) {
      getBalance();
      const checkFees = async () => {
        const [err1, feesEnabled] = await wallet.safeCallContract({
          name: "Deposit",
          method: "feeEnabled",
          args: [],
        });

        console.log("🚀 ~ getBalance ~ feesEnabled:", feesEnabled);
        if (feesEnabled) {
          const [err1, feesFinal] = await wallet.safeCallContract({
            name: "Deposit",
            method: "withdrawFee",
            args: [],
          });
          console.log("🚀 ~ getBalance ~ fees:", feesFinal);
          setFees(Number(ethers.utils.formatEther(feesFinal)));
        }
      };
      checkFees();
    }
  }, [wallet.address, wallet.chainId, newBalance]);
  const chain = useMemo(
    () => chains.find((item) => item.id == wallet.chainId),
    [wallet.chainId]
  );
  const MAX_TOTAL_BALANCE = chain?.features?.isMainnet ? 1000000 : 100000; // 1M for mainnet, 100K for testnet

  const handleDeposit = async () => {
    try {
      // Validate deposit amount
      const depositAmountNum = Number(depositAmount);
      if (isNaN(depositAmountNum) || depositAmountNum <= 0) {
        toast.error("Please enter a valid deposit amount");
        return;
      }

      const currentBalance = Number(offChainBalance);
      const maxAllowedDeposit =
        currentBalance === 0
          ? MAX_TOTAL_BALANCE
          : Math.max(0, MAX_TOTAL_BALANCE - currentBalance);

      if (depositAmountNum > maxAllowedDeposit) {
        toast.error(
          `Maximum deposit allowed is ${maxAllowedDeposit} ${
            currentBalance === 0
              ? "for new deposits"
              : "to stay within 10M limit"
          }`
        );
        return;
      }

      setIsLoading(true);
      const depositAddress = chain?.contracts?.Deposit?.address;

      if (!depositAddress) {
        toast.error("Deposit address not found for this chain");
        setIsLoading(false);
        return;
      }

      const [err1, tokenAllowance] = await wallet.safeCallContract({
        name: "Token3",
        method: "allowance",
        args: [wallet.address, depositAddress],
      });

      if (err1) {
        setIsLoading(false);
        return;
      }

      const depositAmountWei = ethers.utils.parseEther(depositAmount);

      if (tokenAllowance && tokenAllowance.lt(depositAmountWei)) {
        const [err2, approveTx] = await wallet.safeCallContract({
          name: "Token3",
          method: "approve",
          args: [depositAddress, depositAmountWei],
        });

        if (err2) {
          setIsLoading(false);
          return;
        }

        await approveTx.wait();
      }

      const [err3, tx] = await wallet.safeCallContract({
        name: "Deposit",
        method: "deposit",
        args: [chain?.contracts?.Token3?.address, depositAmountWei],
      });

      if (err3) {
        setIsLoading(false);
        return;
      }

      // Call deposit API after transaction is sent
      await axios.post("/api/game/wallet/deposit", {
        txHash: tx.hash,
        gameType: "roulette",
        chainId: wallet.chainId,
        tokenAddress: chain?.contracts?.Token3?.address,
        tokenName: "BlazpayPoints",
        amount: depositAmount,
      });

      // Wait for transaction confirmation
      await tx.wait();

      // Update transaction status
      const data = await axios.patch(
        `/api/game/wallet/transaction/${tx.hash}`,
        {
          status: "completed",
          type:"deposit"
        }
      );
      // console.log("🚀 ~ handleDeposit ~ data:", data);

      // Refresh the on-chain balance
      const [err4, newBalance] = await wallet.safeCallContract({
        name: "Token3",
        method: "balanceOf",
        args: [wallet.address],
      });

      if (!err4) {
        setBalance(Number(ethers.utils.formatEther(newBalance)));
        setAffectedBalance(Date.now() + Math.random());
      }

      setDepositAmount("");
      setIsLoading(false);
      auth.updateTokens();
      toast.success("Deposit successful!");
    } catch (error) {
      console.error(error);
      setIsLoading(false);
      toast.error("Deposit failed");
    }
  };

  const handleWithdraw = async () => {
    try {
      setIsLoading(true);
      const withdrawAmountWei = ethers.utils.parseEther(withdrawAmount);
      try {
        const response = await axios.get(
          `/api/game/wallet?chainId=${wallet.chainId}&gameType=roulette`
        );
        const balance = ethers.utils.parseEther(response.balance.toString());
        console.log("🚀 ~ handleWithdraw ~ balance:", balance);

        if (balance.lt(withdrawAmountWei)) {
          toast.error("Insufficient balance for withdrawal");
          setIsLoading(false);
          return;
        }
      } catch (error) {
        console.error("Error fetching balance:", error);
        toast.error("Failed to fetch balance");
        setIsLoading(false);
        return;
      }

      // Check if fee is enabled and get fee amount
      const [err2, feeEnabled] = await wallet.safeCallContract({
        name: "Deposit",
        method: "feeEnabled",
        args: [],
      });

      if (err2) {
        setIsLoading(false);
        return;
      }

      let txOptions = {};
      if (feeEnabled) {
        const [err3, withdrawFee] = await wallet.safeCallContract({
          name: "Deposit",
          method: "withdrawFee",
          args: [],
        });
        console.log("🚀 ~ handleWithdraw ~ withdrawFee:", withdrawFee);

        if (err3) {
          setIsLoading(false);
          return;
        }

        const feeAmount = ethers.BigNumber.from(withdrawFee);
        txOptions = { value: feeAmount };
      }

      // Execute withdrawal
      const [err4, tx] = await wallet.safeCallContract({
        name: "Deposit",
        method: "withdraw",
        args: [
          chain?.contracts?.Token3?.address,
          withdrawAmountWei,
          wallet.address,
          txOptions,
        ],
      });

      if (err4) {
        setIsLoading(false);
        return;
      }

      // Call withdraw API after transaction is sent
      await axios.post("/api/game/wallet/withdraw", {
        txHash: tx.hash,
        gameType: "roulette",
        chainId: wallet.chainId,
        tokenAddress: chain?.contracts?.Token3?.address,
        tokenName: "BlazpayPoints",
        amount: withdrawAmount,
      });

      // Wait for transaction confirmation
      await tx.wait();

      // Update transaction status
      const data = await axios.patch(
        `/api/game/wallet/transaction/${tx.hash}`,
        {
          status: "completed",
          type:"withdraw"
        }
      );
      console.log("🚀 ~ handleWithdraw ~ data:", data);

      const newBal = await axios.get(
        `/api/game/wallet?chainId=${wallet.chainId}&gameType=roulette`
      );
      console.log("🚀 ~ handleWithdraw ~ balance:", newBal);
      setOffChainBalance(newBal?.balance);
      setAffectedBalance(Date.now() + Math.random());
      toast.success("Withdrawal successful!");
      setIsLoading(false);
      auth.updateTokens();
      setWithdrawAmount("");
    } catch (error) {
      console.error("Error during withdrawal:", error);
      toast.error("Withdrawal failed");
      setIsLoading(false);
    }
  };

  const TABS = [
    {
      value: "deposit",
      element: (
        <Deposit
          handleDeposit={handleDeposit}
          depositAmount={depositAmount}
          setDepositAmount={setDepositAmount}
          isLoading={isLoading}
          chain={chain}
          balance={balance}
          logoUrl={logoUrl}
          offChainBalance={offChainBalance}
          MAX_TOTAL_BALANCE={MAX_TOTAL_BALANCE}
        />
      ),
    },
    {
      value: "withdraw",
      element: (
        <Withdraw
          handleWithdraw={handleWithdraw}
          depositAmount={depositAmount}
          setWithdrawAmount={setWithdrawAmount}
          isLoading={isLoading}
          chain={chain}
          fees={fees}
          balance={offChainBalance}
          logoUrl={logoUrl}
        />
      ),
    },
  ];
  return (
    <>
      {isActive ? (
        <div
          className={`${className} pt-2 z-20 border-[0.8px] border-[#434343] xs:h-[420px] h-[380px] sm:w-[340px] w-[300px] bg-[#000] rounded-xl`}
        >
          <ul className="w-full flex xs:text-[14px] text-xs font-redHat font-bold items-center">
            <li
              role="button"
              aria-label="Wallet Menu"
              onClick={(e) => {
                e.stopPropagation();
                setSelected("deposit");
              }}
              className={`cursor-pointer text-[#dbdbdb] transition-all duration-[250ms] ease w-full xs:py-3 py-2 text-center border-b ${
                selected === "deposit"
                  ? "  border-b-[#200052]"
                  : "border-[#1f1f1f]"
              } `}
            >
              Deposit
            </li>
            <li
              role="button"
              aria-label="Wallet Menu"
              onClick={(e) => {
                e.stopPropagation();
                setSelected("withdraw");
              }}
              className={`cursor-pointer text-[#dbdbdb] transition-all duration-[250ms] ease w-full xs:py-3 py-2 text-center border-b ${
                selected === "withdraw"
                  ? " border-b-[#200052]"
                  : "border-[#1f1f1f]"
              } `}
            >
              Withdraw
            </li>
          </ul>
          {TABS.map(
            (tab) => tab.value === selected && <div>{tab.element}</div>
          )}
        </div>
      ) : null}
    </>
  );
};

export default RouletteWallet;

const Deposit = ({
  handleDeposit,
  depositAmount,
  setDepositAmount,
  isLoading,
  chain,
  balance,
  logoUrl,
  offChainBalance,
  MAX_TOTAL_BALANCE,
}) => {
  const [isActiveChainList, setIsActiveChainList] = useState(false);
  const [selectedChain, setSelectedChain] = useState(null);
  const handleKeyDown = (e) => {
    if (e.key == "Enter") {
      handleDeposit();
    }
  };
  return (
    <div className="w-full relative  pt-4 flex flex-col items-start px-5">
      <div className="flex pl-3 items-center gap-x-2 xs:mb-3 mb-2">
        <h1 className="w-full text-left  xs:text-[14px] text-xs text-[#ebebeb]">
          On-Chain Balance : <span className="font-semibold">{balance}</span>
        </h1>
        <img className="h-3" src={logoUrl} alt="" />
      </div>
      <ChainSelect
        isActive={isActiveChainList}
        setIsActive={setIsActiveChainList}
        selectedChain={selectedChain}
      />
      {isActiveChainList && (
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedChain(chain);
            setIsActiveChainList(false);
          }}
          className="absolute  select-none cursor-pointer inset-x-5 xs:top-24 top-20 z-20 h-fit px-4 xs:py-3 py-2 flex justify-start xs:gap-x-3 gap-x-2 rounded-xl bg-[#333]"
        >
          <img src={chain?.tokenImg} alt="" className="xs:h-6 h-5 xs:w-6 w-5" />
          <span className="text-[#cbcbcb] text-[14px]">{chain.name}</span>
        </div>
      )}
      <InputField
        placeholder={"Enter amount to deposit"}
        value={depositAmount}
        handleChange={setDepositAmount}
        title={"Blaz Points"}
        onKeyDown={handleKeyDown}
        offChainBalance={offChainBalance}
        maxTotalBalance={MAX_TOTAL_BALANCE}
      />
      <button
        disabled={isLoading}
        onClick={handleDeposit}
        className="mt-3 rounded-full xs:text-[14px] text-xs font-semibold w-full text-white bg-[#200052] py-2"
      >
        {isLoading ? "Loading..." : "Deposit"}
      </button>
    </div>
  );
};

const Withdraw = ({
  handleWithdraw,
  withdrawAmount,
  setWithdrawAmount,
  isLoading,
  chain,
  fees,
  balance,
  logoUrl,
}) => {
  const [isActiveChainList, setIsActiveChainList] = useState(false);
  const [selectedChain, setSelectedChain] = useState(null);
  const handleKeyDown = (e) => {
    if (e.key == "Enter") {
      handleWithdraw();
    }
  };
  return (
    <div className="w-full relative pt-4 flex flex-col items-start px-5">
      <div className="flex pl-3 items-center gap-x-2 xs:mb-3 mb-2">
        <h1 className="w-full text-left  xs:text-[14px] text-xs text-[#ebebeb]">
          Balance : <span className="font-semibold">{balance}</span>
        </h1>
        <img className="h-3" src={logoUrl} alt="" />
      </div>
      <ChainSelect
        isActive={isActiveChainList}
        setIsActive={setIsActiveChainList}
        selectedChain={selectedChain}
      />
      {isActiveChainList && (
        <div
          role="button"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedChain(chain);
            setIsActiveChainList(false);
          }}
          className="absolute select-none cursor-pointer inset-x-5 xs:top-24 top-20 z-20 h-fit px-4 xs:py-3 py-2 flex justify-start xs:gap-x-3 gap-x-2 rounded-xl bg-[#333]"
        >
          <img src={chain?.tokenImg} alt="" className="xs:h-6 h-5 xs:w-6 w-5" />
          <span className="text-[#cbcbcb] text-[14px]">{chain.name}</span>
        </div>
      )}
      <InputField
        onKeyDown={handleKeyDown}
        placeholder={"Enter amount to withdraw"}
        value={withdrawAmount}
        handleChange={setWithdrawAmount}
        title={"Amount to Withdraw"}
      />
      <button
        disabled={isLoading}
        onClick={handleWithdraw}
        className="mt-3 rounded-full xs:text-[14px] text-xs font-semibold w-full text-white bg-[#200052] py-2 "
      >
        {isLoading ? "Loading..." : "Withdraw"}
      </button>
      <span className="w-full text-center mt-2 text-xs text-[#999999]">
        Blazpay Fees : {fees} {chain?.nativeCurrency?.symbol}
      </span>
    </div>
  );
};

const ChainSelect = ({ selectedChain, isActive, setIsActive }) => {
  const wallet = useWallet();
  const handleChainSelect = () => {
    if (!wallet?.isConnected) {
      toast.info("First select chain!");
      return;
    }
    setIsActive((x) => !x);
  };
  return (
    <div className="w-full text-[#999999] rounded-full bg-[#1c1c1c] xsLpy-3 py-2 xs:px-6 px-5 2xl:text-base xs:text-[14px] text-xs flex items-center justify-between">
      <h1>{selectedChain ? selectedChain.name : "Select Chain"}</h1>
      <IoIosArrowDown
        role="button"
        aria-label="ChainList Button"
        onClick={handleChainSelect}
        className={`${
          isActive && "rotate-180"
        } transition-all duration-250 ease text-xl cursor-pointer`}
      />
    </div>
  );
};

const InputField = ({
  value,
  handleChange,
  placeholder,
  title,
  onKeyDown,
  offChainBalance,
  maxTotalBalance,
}) => {
  const maxDeposit =
    offChainBalance === 0
      ? maxTotalBalance
      : Math.max(0, maxTotalBalance - Number(offChainBalance));

  return (
    <>
      <h2 className="xs:mt-3 mt-2 relative left-3 text-xs text-[#cbcbcb] font-semibold">
        {title}
      </h2>
      <input
        onKeyDown={onKeyDown}
        type="text"
        placeholder={placeholder}
        value={value}
        onChange={(e) => {
          const val = e.target.value;
          if (
            !val ||
            !maxDeposit ||
            title !== "Blaz Points" ||
            Number(val) <= maxDeposit
          ) {
            handleChange(val);
          }
        }}
        className="w-full outline-none border-none xs:mt-2 mt-1.5 xs:px-6 px-5  2xl:text-base xs:text-[14px] text-xs text-[#999999] placeholder:text-[#999999] bg-[#1c1c1c] rounded-full xs:py-3 py-2.5"
      />
      {maxDeposit !== null && title === "Blaz Points" && (
        <div className="text-xs text-[#999999] ml-3 mt-1">
          Max deposit allowed: {maxDeposit}
        </div>
      )}
    </>
  );
};
